import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { PhotoUploader } from "@/components/common/PhotoUploader";
import { techniciansService } from "@/services/technicians.service";
import { getErrorMessage } from "@/services/api";
import { SPECIALTY_OPTIONS, STATUS_OPTIONS } from "@/config/constants";

const schema = z.object({
    name: z.string().min(2, "Informe o nome"),
    cpf: z.string().optional().nullable(),
    phone: z.string().optional().nullable(),
    email: z.string().email("E-mail inválido").optional().or(z.literal("")).nullable(),
    engineering_specialty: z.string().optional().nullable(),
    professional_registration: z.string().optional().nullable(),
    position: z.string().optional().nullable(),
    status: z.string(),
    photo_url: z.string().optional().nullable(),
    digital_signature_url: z.string().optional().nullable(),
});

function Field({ label, error, children, className = "" }) {
    return (
        <div className={`space-y-1.5 ${className}`}>
            <Label className="text-sm font-medium">{label}</Label>
            {children}
            {error && <div className="text-xs text-danger">{error}</div>}
        </div>
    );
}

export default function TechnicianFormPage() {
    const { id } = useParams();
    const isEdit = !!id;
    const navigate = useNavigate();
    const qc = useQueryClient();

    const detail = useQuery({
        queryKey: ["technician", id],
        queryFn: () => techniciansService.get(id),
        enabled: isEdit,
    });

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            name: "",
            cpf: "",
            phone: "",
            email: "",
            engineering_specialty: "",
            professional_registration: "",
            position: "",
            status: "active",
            photo_url: "",
            digital_signature_url: "",
        },
    });

    React.useEffect(() => {
        if (isEdit && detail.data) reset({ ...detail.data });
    }, [isEdit, detail.data, reset]);

    const save = useMutation({
        mutationFn: (data) => {
            const payload = { ...data };
            Object.keys(payload).forEach((k) => {
                if (payload[k] === "") payload[k] = null;
            });
            return isEdit ? techniciansService.update(id, payload) : techniciansService.create(payload);
        },
        onSuccess: () => {
            toast.success(isEdit ? "Técnico atualizado." : "Técnico criado.");
            qc.invalidateQueries({ queryKey: ["technicians"] });
            navigate("/technicians");
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao salvar técnico.")),
    });

    return (
        <form onSubmit={handleSubmit(save.mutate)} className="space-y-6">
            <PageHeader
                title={isEdit ? "Editar técnico" : "Novo técnico"}
                description="Cadastro do técnico ou engenheiro."
                backTo="/technicians"
                breadcrumb={[
                    { label: "Início", to: "/dashboard" },
                    { label: "Técnicos", to: "/technicians" },
                    { label: isEdit ? "Editar" : "Novo" },
                ]}
                actions={
                    <>
                        <Button type="button" variant="ghost" onClick={() => navigate("/technicians")}>
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            className="bg-secondary text-secondary-foreground hover:opacity-90"
                            disabled={save.isPending}
                            data-testid="technician-save-button"
                        >
                            {save.isPending ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <Save className="mr-2 h-4 w-4" />
                            )}
                            Salvar
                        </Button>
                    </>
                }
            />

            <div className="grid gap-6 lg:grid-cols-3">
                <div className="space-y-6 lg:col-span-2">
                    <Card className="rounded-xl border shadow-sm">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold">Dados pessoais</CardTitle>
                        </CardHeader>
                        <CardContent className="grid gap-4 md:grid-cols-2">
                            <Field label="Nome *" error={errors.name?.message} className="md:col-span-2">
                                <Input {...register("name")} data-testid="technician-name" />
                            </Field>
                            <Field label="CPF">
                                <Input {...register("cpf")} placeholder="000.000.000-00" data-testid="technician-cpf" />
                            </Field>
                            <Field label="Telefone">
                                <Input {...register("phone")} />
                            </Field>
                            <Field label="E-mail" error={errors.email?.message} className="md:col-span-2">
                                <Input type="email" {...register("email")} />
                            </Field>
                        </CardContent>
                    </Card>

                    <Card className="rounded-xl border shadow-sm">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold">Dados profissionais</CardTitle>
                        </CardHeader>
                        <CardContent className="grid gap-4 md:grid-cols-2">
                            <Field label="Especialidade">
                                <Select
                                    value={watch("engineering_specialty") || ""}
                                    onValueChange={(v) => setValue("engineering_specialty", v)}
                                >
                                    <SelectTrigger data-testid="technician-specialty">
                                        <SelectValue placeholder="Selecione" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SPECIALTY_OPTIONS.map((s) => (
                                            <SelectItem key={s} value={s}>
                                                {s}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Cargo / Posição">
                                <Input {...register("position")} />
                            </Field>
                            <Field label="Registro Profissional (CREA)">
                                <Input {...register("professional_registration")} />
                            </Field>
                            <Field label="Status">
                                <Select value={watch("status")} onValueChange={(v) => setValue("status", v)}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {STATUS_OPTIONS.map((s) => (
                                            <SelectItem key={s.value} value={s.value}>
                                                {s.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6 lg:col-span-1">
                    <Card className="rounded-xl border shadow-sm">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold">Foto</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <PhotoUploader
                                value={watch("photo_url")}
                                onChange={(v) => setValue("photo_url", v)}
                                testId="technician-photo-uploader"
                            />
                        </CardContent>
                    </Card>
                    <Card className="rounded-xl border shadow-sm">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold">Assinatura Digital</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <PhotoUploader
                                value={watch("digital_signature_url")}
                                onChange={(v) => setValue("digital_signature_url", v)}
                                testId="technician-signature-uploader"
                            />
                        </CardContent>
                    </Card>
                </div>
            </div>
        </form>
    );
}
